import type { Ref } from 'vue'

import type { INetworkActivity } from '~/features-swym/network-activity/interfaces/network-activity'

import type { ControllerFetchOptions } from '~/features-shared/content/api/interfaces/params'
import { ORDERFIELD } from '~/features-shared/content/api/interfaces/params'
import type { IContent } from '~/features-shared/content/interfaces/content'
import { contentsMap, mergeContent } from '~/features-shared/content/stores/contents-db'
import type { ServiceFetchControllerDefault } from '~/features-shared/content-feed/controllers/services-fetch-controller-default'
import type { IEntity, SUBJECT_6W_URI } from '~/features-shared/libs/interfaces/entity'
import { useTaggable } from '~/features-shared/tags/composables/taggable'

import type { IExaleadGroup } from '~/features-generic/services/interfaces/exalead-groups'

// Interfaces
interface FetchOpts extends Omit<ControllerFetchOptions, 'limit'> {
  context: string
  forceExalead?: boolean
}

interface FetchNextOpts extends Omit<ControllerFetchOptions, 'limit'> {
  context: string
  refresh?: boolean
  forceExalead?: boolean
}

export function useRefineGlobalContentsStore<T extends IEntity>(service: ServiceFetchControllerDefault<T>, avoidGlobalStorage?: boolean) {
  const taggable = useTaggable({}) // Gain tagging capabilities

  type StoredContentType = T extends INetworkActivity ? T : SUBJECT_6W_URI

  const states = {
    contents: ref<Map<string, StoredContentType[]>>(new Map()) as Ref<Map<string, StoredContentType[]>>,
    selectedContext: ref<string>(),
    selectedSort: ref<ORDERFIELD>(ORDERFIELD.CREATION),
    query: ref(''),
    totals: ref<Record<string, number>>({}),
    pages: reactive<Map<string, number>>(new Map()),
    groups: reactive<Map<string, IExaleadGroup[]>>(new Map()),
    limit: 8,
    tagsFetchOptions: <FetchOpts | undefined>undefined,
  }

  const getters = {
    getGroups: (context?: string) => {
      return states.groups.get(context || '') || []
    },
    getContents: (context: string): T[] => {
      const contentsOrIds = states.contents.value.get(context) || []

      if (avoidGlobalStorage)
        return contentsOrIds as T[]

      return contentsOrIds
        .map(id => contentsMap.get(id) as unknown as T)
        .filter(m => m)
    },
  }

  const computations = {
    currentContents: computed<T[]>(() => states.selectedContext.value
      ? (getters.getContents(states.selectedContext.value) || [])
      : []),
    page: computed<number>(() => states.selectedContext.value
      ? (states.pages.get(states.selectedContext.value) || 0)
      : 0),
    total: computed<number>(() => states.selectedContext.value
      ? (states.totals.value[states.selectedContext.value] || 0)
      : 0),
  }

  const methods = {
    clear(c?: string) {
      const context = c || states.selectedContext.value
      if (context) {
        states.contents.value.set(context, [])
        states.pages.set(context, 0)
        // states.groups.delete(context)
        states.totals.value[context] = 0
        states.tagsFetchOptions = undefined
      }
    },
    clearGroups(c?: string) {
      const context = c || states.selectedContext.value
      if (context)
        states.groups.delete(context)
    },
    insert(
      context: string,
      newContents: StoredContentType[],
      position = 0,
    ) {
      const contentList = states.contents.value.get(context) || []
      if (Array.isArray(contentList)) {
        const contentToAdd = newContents.filter(el => !contentList.includes(el))
        contentList.splice(
          position,
          0,
          ...contentToAdd,
        )
      }
    },
    add(
      context: string,
      newContents: T[],
      page: number,
    ) {
      const contents = states.contents.value.get(context) || []

      newContents.forEach((content) => {
        if (avoidGlobalStorage) {
          contents.push(content as StoredContentType)
        }
        else {
          mergeContent(content as unknown as IContent)
          if (!contents.includes(content.subject_uri as StoredContentType))
            contents.push(content.subject_uri as StoredContentType)
        }
      })

      states.contents.value.set(context, contents)

      // Don't do it here ! When fetching both communities at the same time
      // (eg. because the user switch quickly between two communities)
      // You can mix the feed ! (eg. display feed A while you are on feed B)
      // states.selectedCommunityUri.value = communityUri
      states.pages.set(context, page)
    },
    update(context: string) {
      states.selectedContext.value = context
    },
  }

  const actions = {
    /**
     * DO NOT USE refineQuery => it will be removed soon !!!
     */
    fetch: async ({ context, ...options }: FetchOpts) => {
      if (options.page > computations.page.value) {
        const newContents = await service.fetch({ limit: states.limit, ...options })
        // Store the options to fetch tags later
        if (!states.tagsFetchOptions)
          states.tagsFetchOptions = { context, ...options }

        if (newContents.data) {
          methods.add(
            context,
            newContents.data,
            options.page,
          )
        }

        return newContents
      }
    },
    async fetchTags(tagsFetchOptions?: FetchOpts) {
      // call a separate API to get the associated tags
      const options = tagsFetchOptions ?? states.tagsFetchOptions
      if (options && service.shouldFetchTags(options)) {
        const response = await service.fetchTags?.(options)

        if (response) {
          states.groups.set(options.context, response.groups)
          taggable.fetchTagsSeparately?.(() => Promise.resolve(response))
        }

        // Set to a higher value to not fetch tags anymore unless reset is done
        options.page = 2 // Number.MAX_SAFE_INTEGER
      }
    },
    fetchNext: async ({
      context,
      refine,
      sort = ORDERFIELD.CREATION,
      query = '#all',
      refresh = false,
      refineQuery = '',
      defaultRefine = [],
      defaultQuery = [],
      withDraft = false,
      forceExalead = false,
      defaultParams = {},
      withSubTypes,
      derivedTypeId,
      onlyFavoriteCommunities = false,
    }: Omit<FetchNextOpts, 'page'>) => {
      states.selectedContext.value = context
      if (sort !== states.selectedSort.value
        || query !== states.query.value
        || refresh) {
        methods.clear(context)
        states.selectedSort.value = sort
        states.query.value = query
      }

      return actions.fetch({
        context,
        page: computations.page.value + 1,
        sort: states.selectedSort.value,
        query: states.query.value,
        refine,
        defaultRefine,
        defaultQuery,
        refineQuery,
        defaultParams,
        withDraft,
        forceExalead,
        withSubTypes,
        derivedTypeId,
        onlyFavoriteCommunities,
      })
    },
  }

  return {
    // States
    context: states.selectedContext,
    limit: states.limit,
    groups: states.groups,
    states,

    // Tags specific values
    tags: taggable.tags,
    tagsWithSubjectURIData: taggable.tagsWithSubjectURIData,
    tagsWithoutSubjectURIData: taggable.tagsWithoutSubjectURIData,

    // Computed
    total: computations.total,
    currentContents: computations.currentContents,

    // Getters
    getContents: getters.getContents,
    getGroups: getters.getGroups,

    // Actions
    doFetchNext: actions.fetchNext,
    doFetchMeta: actions.fetchTags,
    doInsert: methods.insert,
    doClear: methods.clear,
    doClearGroups: methods.clearGroups,
  }
}
